import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardedRef,
  type ReactElement,
} from "react";
import { Search } from "lucide-react";
import { Dialog, IconButton, useViewport } from "@primitives/index";
import {
  AccountMenu,
  CreateActions,
  RealtimeStatusIndicator,
  SearchBar,
  type AccountMenuProps,
  type RealtimeIndicatorState,
  type SearchBarHandle,
  type SearchBarProps,
  type WorkflowSearchResult,
} from "@compositions/index";
import { TopBar } from "./TopBar";

/**
 * WorkflowTopBar: the workflow workspace's chrome, per viewport class.
 *
 * The bar holds what belongs to the whole session: the wordmark, search, the
 * connection state, Ask, New, and the account. Next lives in the queue-focus
 * header and the workflow scene header, not here.
 *
 *  - `expanded`+  centered search well, live dot + label, account trigger
 *                 with name.
 *  - `medium`     search fills the middle, live dot, account trigger.
 *  - `compact`    a search trigger that opens a sheet, live dot, account
 *                 trigger. The account menu presents as a bottom sheet.
 *
 * `focusSearch()` on the handle serves the `/` shortcut: it focuses the
 * input, or opens the sheet on `compact`.
 */

export type WorkflowSearchProps = Omit<
  SearchBarProps,
  "placeholder" | "resultsPresentation" | "autoFocus"
>;

export interface WorkflowTopBarProps {
  readonly search: WorkflowSearchProps;
  readonly realtimeState: RealtimeIndicatorState;
  readonly account: AccountMenuProps;
  readonly onAsk: () => void;
  readonly onCreate: () => void;
}

export interface WorkflowTopBarHandle {
  focusSearch(): void;
}

const SEARCH_PLACEHOLDER = "Search by entity, name, or title…";

export const WorkflowTopBar = forwardRef<WorkflowTopBarHandle, WorkflowTopBarProps>(
  function WorkflowTopBar(props, ref) {
    const { viewport, isAtLeast } = useViewport();
    if (viewport === "compact") {
      return <CompactWorkflowTopBar {...props} handleRef={ref} />;
    }
    return <WideWorkflowTopBar {...props} handleRef={ref} labeled={isAtLeast("expanded")} />;
  },
);

type HandleRef = ForwardedRef<WorkflowTopBarHandle>;

function WideWorkflowTopBar({
  search,
  realtimeState,
  account,
  onAsk,
  onCreate,
  labeled,
  handleRef,
}: WorkflowTopBarProps & {
  readonly labeled: boolean;
  readonly handleRef: HandleRef;
}): ReactElement {
  const searchRef = useRef<SearchBarHandle>(null);
  useImperativeHandle(handleRef, () => ({ focusSearch: () => searchRef.current?.focus() }), []);

  return (
    <TopBar
      search={<SearchBar ref={searchRef} {...search} placeholder={SEARCH_PLACEHOLDER} />}
      actions={
        <>
          <CreateActions onAsk={onAsk} onCreate={onCreate} labeled={labeled} />
          <RealtimeStatusIndicator state={realtimeState} compact={!labeled} />
          <AccountMenu {...account} />
        </>
      }
    />
  );
}

function CompactWorkflowTopBar({
  search,
  realtimeState,
  account,
  onAsk,
  onCreate,
  handleRef,
}: WorkflowTopBarProps & { readonly handleRef: HandleRef }): ReactElement {
  const [searchOpen, setSearchOpen] = useState(false);
  useImperativeHandle(handleRef, () => ({ focusSearch: () => setSearchOpen(true) }), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    search.onSearch("");
  }, [search]);

  const handleSelect = useCallback(
    (result: WorkflowSearchResult) => {
      closeSearch();
      search.onSelect(result);
    },
    [closeSearch, search],
  );

  return (
    <>
      <TopBar
        actions={
          <>
            <IconButton
              icon={Search}
              aria-label="Search workflows"
              tooltip="Search (/)"
              onClick={() => setSearchOpen(true)}
            />
            <CreateActions onAsk={onAsk} onCreate={onCreate} labeled={false} />
            <RealtimeStatusIndicator state={realtimeState} compact />
            <AccountMenu {...account} />
          </>
        }
      />

      <Dialog
        open={searchOpen}
        onClose={closeSearch}
        title="Search"
        presentation="sheet"
        size="md"
        testId="search-sheet"
      >
        <SearchBar
          {...search}
          onSelect={handleSelect}
          placeholder={SEARCH_PLACEHOLDER}
          resultsPresentation="inline"
          autoFocus
        />
      </Dialog>
    </>
  );
}
